import { loadStripe } from '@stripe/stripe-js';

const STRIPE_JS_URL = 'https://js.stripe.com/v3';
const DEFAULT_RETRIES = 2;
const DEFAULT_BASE_DELAY_MS = 1000;

const wait = (ms) =>
  new Promise((resolve) => {
    setTimeout(resolve, ms);
  });

// @stripe/stripe-js@1 remembers a failed load for the rest of the page lifetime, so calling
// loadStripe again just returns the same rejection. Retries therefore re-inject the script tag
// ourselves and build the instance from window.Stripe once it is available.
const injectStripeScript = () =>
  new Promise((resolve, reject) => {
    document.querySelectorAll(`script[src^="${STRIPE_JS_URL}"]`).forEach((script) => script.remove());

    const script = document.createElement('script');
    script.src = STRIPE_JS_URL;
    script.addEventListener('load', () => {
      if (window.Stripe) {
        resolve(window.Stripe);
      } else {
        reject(new Error('Stripe.js not available'));
      }
    });
    script.addEventListener('error', () => {
      reject(new Error('Failed to load Stripe.js'));
    });
    (document.head || document.body).appendChild(script);
  });

/**
 * Loads Stripe.js with retries and never rejects: a load that still fails after every retry
 * resolves to `null`, which `<Elements stripe={null}>` accepts and which the card forms already
 * guard against (they refuse to submit while `stripe` is null). This keeps a blocked or flaky
 * Stripe.js download from surfacing as an unhandled rejection for users who never reach billing.
 *
 * @param {string} publishableKey Stripe publishable key.
 * @param {{ retries?: number, baseDelayMs?: number }} [options]
 * @returns {Promise<import('@stripe/stripe-js').Stripe | null>}
 */
export default async function loadStripeWithRetry(
  publishableKey,
  { retries = DEFAULT_RETRIES, baseDelayMs = DEFAULT_BASE_DELAY_MS } = {},
) {
  try {
    return await loadStripe(publishableKey);
  } catch (_error) {
    // fall through to the manual retries below
  }

  for (let attempt = 0; attempt < retries; attempt += 1) {
    await wait(baseDelayMs * 2 ** attempt);
    try {
      const Stripe = await injectStripeScript();
      return Stripe(publishableKey);
    } catch (_error) {
      // try again until retries are exhausted
    }
  }

  return null;
}
